import { createRef, forwardRef, useMemo, useRef, useState } from "react";
import type React from "react";
import { Plus } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import useTranslation from "../../client/hooks/useTranslation";
import { cn } from "../../client/utils";
import { AnimatedBeam } from "../../landing-page/components/ui/animated-beam";
import {
  BEAM_CONTAINER_HEIGHT_PX,
  BEAM_CURVATURE_STEP_PX,
  MAX_VISIBLE_ACCOUNTS,
} from "./tradovateBeamLayout";
import { shouldAnimateBeam, BEAM_GRADIENT_OFF } from "./tradovateBeamLight";
import type { SyncTone } from "./tradovateSyncPresentation";

/**
 * Card « une prop firm » de la section synchro, en VUE CARDS.
 *
 * Reprend l'idiome de `IntegrationsCard` (landing) : logo prop firm a gauche,
 * Edgely au centre, comptes synchronises a droite, relies par des faisceaux
 * animes.
 *
 * ── DEUX ECARTS DELIBERES AVEC LA VERSION LANDING ───────────────────────────
 *
 * 1. LES PASTILLES SONT THEMEES, PAS BLANCHES. La landing est verrouillee en
 *    dark : une pastille blanche y ressort toujours. L'app a QUATRE themes,
 *    dont deux clairs (light, sunrise) ou une pastille blanche sur fond clair
 *    disparait purement et simplement. On rend donc la pastille en `bg-card`
 *    avec un anneau `border`, qui contraste sur les quatre. Les logos de prop
 *    firms etant heterogenes (certains blancs/transparents, d'autres sombres),
 *    l'anneau est la seule garantie que le contour du noeud reste visible meme
 *    si le logo lui-meme se fond dans le fond.
 *
 * 2. LES ACTIONS VIVENT SOUS L'ANIMATION, JAMAIS DESSUS. Posees en
 *    surimpression, elles seraient invisibles au premier coup d'oeil et
 *    inaccessibles au tactile (les faisceaux occupent toute la surface). La
 *    barre de pied porte le badge d'etat et les boutons ; l'animation garde
 *    toute sa largeur au-dessus.
 */

/**
 * ⚠️ LE PLAFOND ET LA HAUTEUR SONT SOLIDAIRES — cf. `tradovateBeamLayout.ts`.
 *
 * Ce plafond valait 4, pour un conteneur de 168 px dont `py-4` ne laissait que
 * 136 px utiles. Quatre comptes plus le compteur font pourtant CINQ pastilles,
 * soit 5 x 28 + 4 x 6 = 164 px : la derniere, celle qui porte le « +N », etait
 * coupee par l'`overflow-hidden` de la card. Un utilisateur a 15 comptes en
 * voyait donc 4, sans aucun indice que les 11 autres existaient.
 *
 * Les deux nombres vivent desormais ensemble, sous test.
 */

/**
 * COULEUR DU TRAIT AU REPOS — un gris NEUTRE, et non un token de theme.
 *
 * `AnimatedBeam` ne prend que des chaines de couleur passees en attribut SVG
 * `stroke` : il ne peut pas recevoir une classe Tailwind. On pourrait y injecter
 * `hsl(var(--border))`, mais ce token est justement CONCU pour se fondre dans le
 * fond, et un trait invisible n'est pas un faisceau.
 *
 * Ce gris moyen (slate-500) est le meme des deux cotes de la frontiere
 * clair/sombre : suffisamment fonce pour se detacher des fonds clairs (light,
 * sunrise), suffisamment clair pour se detacher des fonds sombres (dark,
 * jungle). A faible opacite il ne fait qu'esquisser le chemin.
 *
 * Le faisceau ANIME, lui, garde les couleurs de marque par defaut du composant
 * (teal -> bleu), qui sont les memes sur les quatre themes par construction.
 */
const BEAM_PATH_COLOR = "#64748b";

/** Pastille circulaire portant un logo ou du texte. */
const BeamNode = forwardRef<
  HTMLDivElement,
  { className?: string; children: React.ReactNode }
>(({ className, children }, ref) => (
  <div
    ref={ref}
    className={cn(
      // `bg-card` + anneau `border` : lisible sur les 4 themes, la ou une
      // pastille blanche disparait sur light et sunrise.
      "z-10 flex items-center justify-center rounded-full border border-border bg-card shadow-sm",
      className,
    )}
  >
    {children}
  </div>
));
BeamNode.displayName = "BeamNode";

export interface BeamAccount {
  id: string;
  /** Libelle affiche : identifiant broker, tronque par CSS si besoin. */
  label: string;
}

interface Props {
  /** Nom de la prop firm (ou repli « Tradovate » si non rattachee). */
  firmName: string;
  /** Logo resolu, ou null : on retombe alors sur la pastille de couleur. */
  logoSrc: string | null;
  /** Couleur de badge de la prop firm, pour le repli sans logo. */
  logoColor: string | null;
  /**
   * Hote API auquel CETTE connexion parle, choisi a l'OAuth.
   *
   * Deux connexions OAuth distinctes, chacune avec son jeton et son statut :
   * une meme prop firm peut donc apparaitre deux fois, une carte par
   * environnement. Sans ce badge, les deux cards sont indiscernables.
   *
   * Optionnel : une connexion mise en cache par une version anterieure du
   * client peut ne pas porter le champ, on masque alors le badge plutot que
   * d'affirmer un environnement qu'on ne connait pas.
   */
  environment?: string | null;
  accounts: BeamAccount[];
  /** Ajouter un compte a CETTE connexion. */
  onAddAccount: () => void;
  /**
   * Ton d'etat de la connexion, tel que resolu par `resolveSyncPresentation`.
   *
   * ⚠️ POURQUOI LA CARD A BESOIN DE CETTE VALEUR, alors qu'elle recoit deja le
   * badge d'etat. `footerStatus` est un `ReactNode` OPAQUE : la card peut le
   * poser dans son pied, elle ne peut rien en LIRE. Or elle doit decider si la
   * lumiere traverse ses faisceaux, ce qui suppose de connaitre l'etat.
   *
   * On passe le `tone` deja calcule, et non `displayStatus` / `realtimeStatus`
   * bruts : la composition des deux axes est la responsabilite de
   * `resolveSyncPresentation`, et la refaire ici garantirait qu'un jour le
   * badge et le faisceau se contredisent.
   *
   * Optionnel : sans valeur, on retombe sur `neutral`, donc faisceau ETEINT.
   * Le defaut se fait du cote sur -- on n'affirme pas un flux qu'on n'a pas
   * constate.
   */
  syncTone?: SyncTone;
  /** Barre de pied : badge d'etat a gauche, actions a droite. */
  footerStatus: React.ReactNode;
  footerActions: React.ReactNode;
  /** Selecteur de rattachement, rendu sous la barre quand il y a lieu. */
  assignSlot?: React.ReactNode;
  /**
   * Variante allegee (mode Lite) : sans bandeau d'environnement ni bouton
   * « Ajouter un compte » dans le pied, l'appelant regroupant ses actions dans
   * `footerActions`. Absente = rendu historique, inchange.
   */
  sober?: boolean;
  /** Actions en coin haut droit de la carte (variante `sober`). */
  cornerActions?: React.ReactNode;
}

export default function TradovateConnectionBeamCard({
  firmName,
  logoSrc,
  logoColor,
  environment,
  accounts,
  onAddAccount,
  syncTone = "neutral",
  footerStatus,
  footerActions,
  assignSlot,
  sober = false,
  cornerActions,
}: Props) {
  const t = useTranslation();

  // ── LIBELLE D'ENVIRONNEMENT ────────────────────────────────────────────────
  //
  // ⚠️ ON N'AFFICHE PAS LE MOT « demo ». C'est le nom d'HOTE chez Tradovate,
  // pas une notion utilisateur. Pour un trader, cet hote est celui ou vivent
  // les comptes d'evaluation ET les comptes finances (PA) ; « live » designe
  // un compte en argent reel. Ecrire « demo » laisserait croire a un compte
  // d'entrainement, ce qui est FAUX pour un compte PA finance — et donc
  // alarmant pour son proprietaire.
  const environmentLabel =
    environment === "demo"
      ? t.tradovate.environmentDemo
      : environment === "live"
        ? t.tradovate.environmentLive
        : null;

  // ── LA LUMIERE NE TRAVERSE QUE SI LA CONNEXION COLLECTE VRAIMENT ──────────
  //
  // La regle vit dans `tradovateBeamLight.ts` (module pur, teste sans React).
  // On n'eteint pas le faisceau : on retire seulement son degrade anime, en
  // passant des couleurs transparentes a `AnimatedBeam`. Le trait au repos,
  // qui lit `pathColor`, subsiste et continue de dire que le LIEN existe.
  //
  // ⚠️ `animated-beam.tsx` est partage avec la landing page, ou l'animation
  // est permanente et voulue : on n'agit que par ses props.
  const beamIsLit = shouldAnimateBeam(syncTone);
  // Non renseignees quand la lumiere passe : `AnimatedBeam` retombe alors sur
  // ses couleurs de marque par defaut (teal -> bleu), qu'on ne recopie pas ici.
  const beamGradient = beamIsLit
    ? {}
    : { gradientStartColor: BEAM_GRADIENT_OFF, gradientStopColor: BEAM_GRADIENT_OFF };

  // HORLOGE COMMUNE A TOUS LES FAISCEAUX DE LA CARD.
  //
  // Chaque `AnimatedBeam` boucle a l'infini depuis SON montage. Or les
  // faisceaux des comptes se remontent quand la liste change (cle = id du
  // compte, refs recreees sur `nodeCount`), donc apres celui de la prop firm :
  // la lumiere passait alors sur un compte AVANT d'avoir traverse prop firm ->
  // Edgely. Une origine commune les cale sur la meme frise ; le degrade etant
  // en `userSpaceOnUse` (balayage sur toute la largeur du conteneur), l'ordre
  // gauche -> droite en decoule.
  const [beamClockStart] = useState(() => performance.now());

  const containerRef = useRef<HTMLDivElement>(null);
  const firmRef = useRef<HTMLDivElement>(null);
  const edgelyRef = useRef<HTMLDivElement>(null);

  const visible = accounts.slice(0, MAX_VISIBLE_ACCOUNTS);
  const hidden = accounts.length - visible.length;
  // Le compteur « +N autres » est un NOEUD comme les autres : il reçoit donc
  // son propre faisceau, sans quoi il flotterait sans lien avec le hub.
  const nodeCount = visible.length + (hidden > 0 ? 1 : 0);

  // ── REFS DES NOEUDS DE DROITE : STABLES, MAIS REDIMENSIONNABLES ───────────
  //
  // `IntegrationsCard` fige son tableau de refs dans un `useRef` parce que sa
  // liste de prop firms est une CONSTANTE de module. Ici la liste est dynamique
  // (un compte importe, un compte desactive) : ce tableau ne grandirait jamais,
  // et `firmRefs.current[i]` rendrait `undefined` pour tout noeud ajoute apres
  // le premier rendu. `AnimatedBeam` lirait alors un `.current` absent et le
  // faisceau ne se dessinerait pas.
  //
  // On garde donc la propriete essentielle — un objet ref STABLE d'un rendu a
  // l'autre, faute de quoi `AnimatedBeam` capturerait un `{ current: null }`
  // neuf a chaque passe — tout en reconstruisant le tableau quand la longueur
  // change. `useMemo` sur `nodeCount` suffit : les refs existantes ne sont pas
  // reutilisees lors d'un changement de taille, mais l'effet de mesure de
  // `AnimatedBeam` se rejoue de toute facon puisque `toRef` change d'identite.
  const accountRefs = useMemo(
    () => Array.from({ length: nodeCount }, () => createRef<HTMLDivElement>()),
    [nodeCount],
  );

  // ANCRAGE SUR LE BORD GAUCHE RÉEL DE CHAQUE PASTILLE.
  //
  // `AnimatedBeam` vise le CENTRE de son `toRef`. On reculait auparavant d'une
  // demi-largeur CONSTANTE (150 / 168 px) : or la pastille est en `max-w-full`
  // et rétrécit dans une carte étroite (texte tronqué). Le point d'arrivée
  // tombait alors AVANT la pastille et le trait s'arrêtait dans le vide (constaté
  // en prod, 2026-10-04). On vise désormais un repère de taille nulle posé sur le
  // bord gauche de la pastille : juste quelle que soit sa largeur réelle.
  const anchorRefs = useMemo(
    () => Array.from({ length: nodeCount }, () => createRef<HTMLSpanElement>()),
    [nodeCount],
  );

  const hasAccounts = nodeCount > 0;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-muted/20">
      {/* Variante sobre : la bande de tête porte les actions (même hauteur que le
          bandeau d'environnement qu'elle remplace, pour ne pas mordre sur les faisceaux). */}
      {sober && (
        <div className="flex h-9 items-center justify-end gap-0.5 px-2 pt-1">{cornerActions}</div>
      )}
      {/* ── BANDEAU DE TETE — L'ENVIRONNEMENT, EN HAUT A GAUCHE ─────────────

          Le badge vivait dans le pied, colle au badge d'etat. Demande produit :
          le remonter en tete de card. C'est aussi plus juste hierarchiquement,
          l'environnement etant une propriete IMMUABLE de la connexion (choisie
          a l'OAuth), la ou le pied porte ce qui VARIE : l'etat de synchro et
          les actions.

          ⚠️ LE BANDEAU EST RENDU MEME SANS BADGE, et c'est la seule subtilite
          de ce bloc. `environmentLabel` vaut `null` sur une connexion mise en
          cache par un client anterieur, qui ne porte pas le champ. Un bandeau
          conditionnel ferait alors une card PLUS COURTE que ses voisines : la
          grille est a trois par ligne, et une rangee en dents de scie est
          exactement ce que la hauteur fixe du conteneur de faisceaux cherche a
          eviter (cf. `BEAM_CONTAINER_HEIGHT_PX`, qui ne gouverne QUE la zone
          d'animation et ignore tout ce qui l'entoure). On reserve donc la
          bande et on masque le seul badge — meme raisonnement que la gouttiere
          de chevron de la vue tableau.

          `h-8` fige cette hauteur independamment du contenu : le badge est en
          `text-[11px]`, mais une traduction plus longue ou un futur contenu ne
          peuvent pas faire grandir la bande d'une card sans les autres. */}
      {!sober && (
      <div className="flex h-8 items-center gap-2 px-4 sm:px-6">
        {/* Tons sourds (`border` + `muted-foreground`) : c'est une
            information, jamais une alerte. Tokens de theme uniquement, pour
            rester lisible sur les quatre themes. */}
        {environmentLabel && (
          <span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
            {environmentLabel}
          </span>
        )}
      </div>
      )}

      {/* ── L'ANIMATION, PLEINE LARGEUR — TROIS PISTES DE GRILLE ────────────
          En flex, seule la colonne des comptes portait `flex-1` : les deux
          autres se reduisaient a leur contenu et `justify-between` les tassait
          contre le bord gauche. Edgely tombait alors au sixieme de la largeur,
          avec un long faisceau vide jusqu'aux comptes.

          Une grille `[auto_1fr_auto]` ne suffirait pas : les pistes `auto` se
          dimensionnent sur leur contenu, donc une prop firm au nom long et une
          liste de comptes larges decaleraient encore le centre. On declare donc
          les deux pistes laterales a `1fr` CHACUNE, avec le hub sur une piste
          `auto` au milieu : les deux flancs recoivent exactement la meme
          largeur, quel que soit leur contenu, et le hub est geometriquement
          centre par construction. */}
      <div
        ref={containerRef}
        // ⚠️ HAUTEUR EN STYLE INLINE, et non en classe Tailwind arbitraire.
        // Elle est DERIVEE de `MAX_VISIBLE_ACCOUNTS` (cf. `tradovateBeamLayout`)
        // et verifiee par un test ; une classe `h-[232px]` serait une copie
        // muette du meme nombre, que le test ne pourrait pas lire.
        //
        // Hauteur UNIQUE, identique sur toutes les cards : la grille est a trois
        // par ligne et des hauteurs au contenu donneraient une rangee irreguliere.
        style={{ height: `${BEAM_CONTAINER_HEIGHT_PX}px` }}
        className="relative grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-4 py-4 sm:px-6"
        // Decoratif : le meme etat est deja lisible en texte dans la barre de
        // pied, on ne le fait pas annoncer deux fois.
        role="img"
        aria-label={t.tradovate.beamFlowLabel}
      >
        {/* Colonne 1 — la prop firm.
            ⚠️ LE LIBELLE EST HORS FLUX, et ce n'est pas un detail de style.
            Empile sous la pastille, il faisait grandir la colonne vers le bas,
            et le centrage vertical remontait la pastille d'une demi-hauteur de
            libelle : le cercle prop firm ne partageait plus la ligne d'horizon
            du cercle Edgely, qui n'a pas de libelle. Le faisceau entre les deux
            partait donc en diagonale. En `absolute`, le libelle n'entre plus
            dans le calcul de hauteur : la pastille est seule a etre centree,
            exactement comme celle d'Edgely. */}
        <div className="relative flex min-w-0 items-center justify-center">
          <BeamNode
            ref={firmRef}
            // PASTILLE BLANCHE ET ROGNAGE AU CERCLE.
            //
            // Les logos de prop firm sont HETEROGENES : certains sont des PNG
            // carres a fond plein (Take Profit Trader), d'autres des traces
            // transparents (Apex). Sur `bg-card` sans rognage, le premier
            // debordait en carre visible hors du cercle tandis que le second
            // flottait au milieu. Un fond blanc unique donne la meme surface a
            // tous, et `overflow-hidden` decoupe le carre au cercle.
            //
            // Marge reduite (`p-1.5`) : `object-contain` laisse deja de l'air
            // autour d'un trace transparent, et 8 px de plus le rendaient
            // minuscule dans sa pastille.
            className="h-12 w-12 overflow-hidden border-transparent bg-white p-1.5 ring-1 ring-black/10 sm:h-14 sm:w-14"
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt=""
                className="h-full w-full object-contain"
                loading="lazy"
              />
            ) : (
              <span
                className={cn(
                  "h-4 w-4 rounded-full",
                  !logoColor && "bg-muted-foreground",
                )}
                style={logoColor ? { backgroundColor: logoColor } : undefined}
              />
            )}
          </BeamNode>
          <span className="absolute left-1/2 top-full w-full max-w-[92px] -translate-x-1/2 translate-y-2 truncate text-center text-[11px] text-muted-foreground sm:max-w-[112px]">
            {firmName}
          </span>
        </div>

        {/* Colonne 2 — Edgely, le hub. Piste `auto` : il n'occupe que sa propre
            largeur, encadre par deux flancs de largeur egale. */}
        <div className="flex items-center justify-center">
          {/* PASTILLE BLANCHE SUR LES 4 THEMES, et non `bg-card`.
              `edgely-logo-dark.png` est un trace a contour fin, presque blanc :
              pose sur un fond clair il DISPARAIT, et sur le fond sombre il se
              devine a peine. `edgely-logo-light.png` (barres pleines + bloc
              teal) est le seul des deux qui soit lisible en autonomie, mais il
              lui faut un fond CLAIR sous ses barres noires. La pastille est
              donc blanche sur les 4 themes, figee hors des tokens : c'est le
              contraste du logo qui la dicte, pas le theme de la page. Le bloc
              teal garde sa couleur d'origine, c'est la marque. */}
          <BeamNode
            ref={edgelyRef}
            className="h-14 w-14 border-transparent bg-white p-2.5 ring-1 ring-black/10 sm:h-16 sm:w-16"
          >
            <img
              src="/logos/edgely-logo-light.png"
              alt="Edgely"
              className="h-full w-full object-contain"
              loading="lazy"
            />
          </BeamNode>
        </div>

        {/* Colonne 3 — les comptes synchronises */}
        <div className="flex min-w-0 flex-col justify-center">
          {hasAccounts ? (
            // LARGEUR BORNEE, et non `w-full`.
            //
            // A pleine largeur les pastilles demarraient juste apres le hub :
            // le faisceau Edgely -> compte etait reduit a presque rien, alors
            // que celui de gauche restait long. On borne donc la pastille et on
            // la cale a droite, ce qui rend au faisceau de droite une longueur
            // comparable a celui de gauche.
            <div className="flex flex-col items-end justify-center gap-1.5">
              {visible.map((account, i) => (
                <BeamNode
                  key={account.id}
                  ref={accountRefs[i]}
                  className="relative h-7 w-[150px] max-w-full justify-start px-2.5 sm:w-[168px]"
                >
                  <span ref={anchorRefs[i]} aria-hidden className="pointer-events-none absolute left-0 top-1/2 h-0 w-0" />
                  <span className="truncate font-mono text-[11px] text-foreground">
                    {account.label}
                  </span>
                </BeamNode>
              ))}
              {hidden > 0 && (
                <BeamNode
                  ref={accountRefs[visible.length]}
                  className="relative h-7 w-[150px] max-w-full justify-start px-2.5 sm:w-[168px]"
                >
                  <span ref={anchorRefs[visible.length]} aria-hidden className="pointer-events-none absolute left-0 top-1/2 h-0 w-0" />
                  <span className="truncate text-[11px] text-muted-foreground">
                    {t.tradovate.beamAccountsMore.replace("{count}", String(hidden))}
                  </span>
                </BeamNode>
              )}
            </div>
          ) : (
            // ZERO COMPTE — on garde l'animation complete (prop firm + Edgely
            // relies) et on explique le vide, plutot que de dessiner un noeud
            // fantome qui ferait croire a un compte.
            // Meme borne que les pastilles : sans elle le texte s'etirait
            // jusqu'au bord de la card et collait au hub.
            // `min-w-0` + bouton compressible : sur une card etroite, le
            // `whitespace-nowrap` du bouton imposait sa largeur a toute la
            // colonne, qui debordait alors sous l'`overflow-hidden` de la card.
            <div className="ml-auto flex w-full min-w-0 max-w-[190px] flex-col items-end gap-1.5 text-right">
              <span className="text-xs font-medium text-foreground">
                {t.tradovate.beamNoAccountsTitle}
              </span>
              <span className="text-[11px] leading-snug text-muted-foreground">
                {t.tradovate.beamNoAccountsHint}
              </span>
              {!sober && <Button
                variant="outline"
                size="sm"
                className="mt-1 h-7 max-w-full"
                onClick={onAddAccount}
              >
                <Plus className="mr-1 h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{t.tradovate.addAccount}</span>
              </Button>}
            </div>
          )}
        </div>

        {/* Faisceau : prop firm -> Edgely. Toujours present, y compris sans
            compte : c'est lui qui dit « la connexion OAuth, elle, est vivante ». */}
        <AnimatedBeam
          containerRef={containerRef}
          fromRef={firmRef}
          toRef={edgelyRef}
          pathColor={BEAM_PATH_COLOR}
          pathOpacity={0.25}
          duration={4}
          clockStart={beamClockStart}
          {...beamGradient}
        />

        {/* Faisceaux : Edgely -> chaque compte. `curvature` etale les traits
            autour du centre pour qu'ils ne se superposent pas.

            ⚠️ L'ETALEMENT EST PASSE DE 16 A 10, PUIS DE 10 A 7 px PAR RANG.
            `AnimatedBeam` place le point de controle a mi-chemin horizontal,
            decale de `curvature` en ordonnee : la FLECHE de la courbe est donc
            un ecart vertical fixe, alors que la corde, elle, s'est raccourcie
            deux fois (le hub a quitte le sixieme gauche pour le milieu, et la
            card retrecit en passant a trois par ligne). A 16 px, le meme
            bombement sur une corde deux fois plus courte se lisait comme un arc
            et non comme un faisceau, d'ou le passage a 10.

            Le passage a 7 repond a une autre cause : le facteur s'applique au
            rang CENTRE `(i - (n-1)/2)`, dont l'amplitude croit avec le nombre
            de noeuds. A 6 noeuds, 10 px donneraient ±25 px d'ecart extreme
            contre ±20 px a 5 noeuds — et ce, alors meme que les pastilles sont
            deja plus ecartees verticalement qu'avant, donc que leurs faisceaux
            divergent naturellement davantage. Le detail du calcul est dans
            `tradovateBeamLayout.ts`. */}
        {anchorRefs.map((ref, i) => (
          <AnimatedBeam
            // Cle portant l'IDENTITE du compte vise, pas seulement son rang :
            // sur un compte importe ou desactive, une cle positionnelle
            // recyclerait l'instance existante et la laisserait dependre du
            // seul changement d'identite de `toRef` pour se remesurer.
            key={visible[i]?.id ?? "overflow"}
            containerRef={containerRef}
            fromRef={edgelyRef}
            toRef={ref}
            curvature={(i - (nodeCount - 1) / 2) * BEAM_CURVATURE_STEP_PX}
            pathColor={BEAM_PATH_COLOR}
            pathOpacity={0.25}
            duration={4}
            delay={i * 0.25}
            clockStart={beamClockStart}
            {...beamGradient}
          />
        ))}
      </div>

      {/* ── BARRE DE PIED — ETAT A GAUCHE, ACTIONS A DROITE ──────────────── */}
      {/* UNE SEULE LIGNE des que la card a sa largeur nominale.
          Le pied etait en `flex-wrap` a trois niveaux, et le contenu du statut
          varie d'une connexion a l'autre (« Trades recus a l'instant » n'existe
          que sur un socket vivant) : le seuil de repli tombait donc a des
          largeurs differentes, et deux cards d'une meme rangee n'avaient pas la
          meme hauteur de pied. On interdit le repli et on laisse le groupe de
          gauche se retrecir (troncature de la fraicheur), les boutons de droite
          gardant leur taille (`shrink-0`). Le texte libre de fraicheur est le
          seul a pouvoir ceder : les deux badges sont `shrink-0`, un etat
          tronque ne voulant rien dire. */}
      {/* ⚠️ REPLI AUTORISE EN DERNIER RECOURS. Sans lui, sous ~420 px (une
          seule card par ligne, mobile) le groupe de gauche se comprimait sous
          la largeur de son badge : « Synchro en direct » se lisait « Sy / en ».
          La grille de la section garantit desormais >= 420 px des qu'il y a
          plusieurs cards par ligne, la ou l'egalite des hauteurs compte ; le
          repli ne survient donc qu'en colonne unique, ou il ne desaligne rien. */}
      <div className={cn("flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-border/60 bg-card/40 px-4 py-2.5 sm:px-6", sober && "min-h-[53px]")}>
        <div
          className={cn(
            // PAS de `min-w-0` / `overflow-hidden` ici : le plancher de ce
            // groupe doit rester la largeur du badge, sinon un pied etroit
            // rogne l'etat au lieu de ne ceder que la fraicheur.
            "flex flex-nowrap items-center gap-2",
            // `TradovateSyncStatus part="badge"` rend une grille
            // `grid-cols-[auto_auto]` (badge + fraicheur) : des pistes `auto`
            // ne se compriment pas, c'est elle qui poussait le pied au repli.
            // On la rend compressible ICI plutot que dans le composant
            // partage, que la vue tableau utilise avec ses propres largeurs.
            //
            // ⚠️ `>div>div`, PAS `>div` : la racine du composant est un
            // `display: contents` (part !== "all"), la grille est son enfant.
            // Un `>div` visait ce wrapper, sans effet. La grille gardait alors
            // `sm:grid-cols-subgrid`, INVALIDE hors d'une grille parente (ici
            // un flex) : Chrome la resout en `none`, la fraicheur passe sous le
            // badge et la colonne s'effondre a sa largeur min-content (79 px),
            // d'ou « Synchro / en direct » sur deux lignes malgre la place.
            "[&>div>div]:grid-cols-[auto_minmax(0,auto)]",
            // Seul le texte libre de fraicheur cede. Il est rendu en `span`
            // ENFANT DIRECT de cette grille, la ou le badge d'etat rend un
            // `div` (cf. components/ui/badge.tsx) : le selecteur ne touche donc
            // que lui, et un etat ne peut pas se retrouver tronque ni replie.
            "[&>div>div>span]:truncate [&>div>div>div]:whitespace-nowrap",
          )}
        >
          {/* L'ENVIRONNEMENT A QUITTE CE PIED pour le bandeau de tete. Il n'en
              reste aucune copie ici : deux fois la meme information dans une
              card de cette taille se lit comme deux informations. */}
          {footerStatus}
        </div>
        <div className="flex shrink-0 flex-nowrap items-center gap-2">
          {footerActions}
          {/* « Ajouter un compte » n'est propose ici que quand la colonne de
              droite ne le porte pas deja (cas zero compte) : deux boutons
              identiques dans la meme card sont une hesitation, pas un choix. */}
          {hasAccounts && !sober && (
            <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={onAddAccount}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              {t.tradovate.addAccount}
            </Button>
          )}
        </div>
      </div>

      {assignSlot && (
        <div className="border-t border-border/60 px-4 py-2.5 sm:px-6">{assignSlot}</div>
      )}
    </div>
  );
}
